import React, { useMemo } from "react";

import { format, parseISO } from "date-fns";
import Chart from "react-apexcharts";
import { useTranslation } from "react-i18next";

import FinanceChartEmpty from "./FinanceChartEmpty";
import CfLoader from "../../common/CfLoader/CfLoader";
import { cssVar, DASHBOARD_CHART_CSS_VARS } from "../../utils/cssVariables";
import { getDateFnsLocale } from "../../utils/getDateFnsLocale";

const CHART_HEIGHT = 320;
const PRIMARY = cssVar("--cf-primary-color");
const SECONDARY = cssVar("--cf-secondary-color");
const GRID_BORDER = cssVar("--card-border-color");

const SettlementTrendChart = ({
  points = [],
  loading = false,
  showDateLabels = true,
}) => {
  const { t, i18n } = useTranslation();
  const dateLocale = useMemo(
    () => getDateFnsLocale(i18n.language),
    [i18n.language],
  );

  const categories = useMemo(
    () =>
      points.map((point) => {
        try {
          return format(parseISO(point.period_start), "MMM d", {
            locale: dateLocale,
          });
        } catch {
          return point.period_start;
        }
      }),
    [points, dateLocale],
  );

  const seriesData = useMemo(
    () => points.map((point) => Number(point.verifications_completed) || 0),
    [points],
  );

  const hasData = seriesData.some((value) => value > 0);

  const options = useMemo(
    () => ({
      chart: {
        type: "area",
        toolbar: { show: false },
        fontFamily: "Inter, sans-serif",
        zoom: { enabled: false },
      },
      stroke: {
        curve: "smooth",
        width: 3,
        colors: [PRIMARY],
      },
      colors: [PRIMARY],
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.35,
          opacityTo: 0.05,
          stops: [0, 90, 100],
          colorStops: [
            [
              { offset: 0, color: PRIMARY, opacity: 0.35 },
              { offset: 100, color: PRIMARY, opacity: 0.05 },
            ],
          ],
        },
      },
      dataLabels: { enabled: false },
      markers: {
        size: 4,
        colors: [PRIMARY],
        strokeColors: SECONDARY,
        strokeWidth: 2,
      },
      tooltip: { theme: "dark" },
      xaxis: {
        categories,
        labels: {
          show: showDateLabels,
          style: {
            colors: cssVar(DASHBOARD_CHART_CSS_VARS.axisLabel),
            fontSize: "12px",
            fontWeight: "500",
          },
        },
        axisTicks: { show: showDateLabels },
      },
      yaxis: {
        labels: {
          style: {
            colors: cssVar(DASHBOARD_CHART_CSS_VARS.gridLabel),
            fontSize: "12px",
          },
        },
      },
      grid: {
        strokeDashArray: 5,
        borderColor: GRID_BORDER,
      },
    }),
    [categories, showDateLabels],
  );

  const series = useMemo(
    () => [
      {
        name: t("finance:total_verifications"),
        data: seriesData,
      },
    ],
    [seriesData, t],
  );

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-6 h-full">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          {t("finance:verification_trend_title")}
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          {t("finance:verification_trend_subtitle")}
        </p>
      </div>
      {loading ? (
        <div
          className="flex items-center justify-center"
          style={{ height: CHART_HEIGHT }}
        >
          <CfLoader />
        </div>
      ) : !hasData ? (
        <FinanceChartEmpty height={CHART_HEIGHT} />
      ) : (
        <Chart
          options={options}
          series={series}
          type="area"
          height={CHART_HEIGHT}
        />
      )}
    </div>
  );
};

export default SettlementTrendChart;
